'use client'

import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

interface UserAgentBlock {
  userAgent: string
  allowPaths: string[]
  disallowPaths: string[]
  crawlDelay: number | null
  lineRange: [number, number]
}

interface DirectiveBreakdownProps {
  blocks: UserAgentBlock[]
  sitemapUrls: string[]
}

export default function DirectiveBreakdown({
  blocks,
  sitemapUrls,
}: DirectiveBreakdownProps) {
  const [expanded, setExpanded] = useState(false)
  const visibleBlocks = expanded ? blocks : blocks.slice(0, 5)

  if (blocks.length === 0) {
    return (
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
        <h2 className='text-lg font-semibold text-foreground mb-2'>
          Directive Breakdown
        </h2>
        <p className='text-sm text-muted-foreground'>
          No user-agent blocks found in robots.txt.
        </p>
      </div>
    )
  }

  return (
    <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
      <h2 className='text-lg font-semibold text-foreground mb-2'>
        Directive Breakdown
      </h2>
      <p className='text-sm text-muted-foreground mb-5'>
        {blocks.length} user-agent block{blocks.length !== 1 ? 's' : ''} found
        in your robots.txt.
      </p>

      <div className='space-y-3'>
        {visibleBlocks.map((block, i) => {
          const activePaths = block.disallowPaths.filter(p => p.length > 0)
          return (
            <div
              key={i}
              className='bg-background border border-border/60 rounded-xl p-4'
            >
              <div className='flex items-center justify-between mb-2'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-semibold text-foreground font-mono'>
                    {block.userAgent === '*' ? '* (wildcard)' : block.userAgent}
                  </span>
                  <span className='text-[10px] text-muted-foreground'>
                    lines {block.lineRange[0]}-{block.lineRange[1]}
                  </span>
                </div>
                {block.crawlDelay !== null && (
                  <span
                    className='text-[10px] font-medium rounded-full px-2 py-0.5'
                    style={{
                      backgroundColor: '#FDC30415',
                      color: '#FDC304',
                    }}
                  >
                    delay: {block.crawlDelay}s
                  </span>
                )}
              </div>

              <div className='flex flex-wrap gap-1.5'>
                {block.allowPaths.map((path, j) => (
                  <span
                    key={`a-${j}`}
                    className='inline-flex items-center text-[11px] font-mono rounded px-2 py-0.5'
                    style={{
                      backgroundColor: '#22c55e15',
                      color: '#22c55e',
                    }}
                  >
                    Allow: {path || '/'}
                  </span>
                ))}
                {activePaths.map((path, j) => (
                  <span
                    key={`d-${j}`}
                    className='inline-flex items-center text-[11px] font-mono rounded px-2 py-0.5'
                    style={{
                      backgroundColor: '#F90B8A15',
                      color: '#F90B8A',
                    }}
                  >
                    Disallow: {path}
                  </span>
                ))}
                {block.allowPaths.length === 0 && activePaths.length === 0 && (
                  <span className='text-[11px] text-muted-foreground'>
                    No directives (empty disallow = allow all)
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {blocks.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className='flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-3'
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {expanded ? 'Show fewer' : `Show all ${blocks.length} blocks`}
        </button>
      )}

      {/* Sitemaps */}
      {sitemapUrls.length > 0 && (
        <div className='mt-4 pt-4 border-t border-border/60'>
          <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2'>
            Sitemap Directives
          </h3>
          <ul className='space-y-1'>
            {sitemapUrls.map((url, i) => (
              <li
                key={i}
                className='text-xs text-foreground/80 font-mono truncate'
              >
                {url}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
